import React from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

/**
 * The MediaGrid component is a grid of {@link MediaGridItem}
 * which are links around images displayed in a grid.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-media-grid.html
 *
 * @param {string} [className] - Additional classes on the component element.
 * @param {MediaGridItem[]} children - A collection of {@link MediaGridItem} components.
 * @param {string} [testId] - `[data-testid]` attribute on the component element.
 * @param {Object} [otherProps] - Other props to be passed to the component element.
 * @return {JSX.Element} - The {@link MediaGrid} component.
 * @constructor
 */
export function MediaGrid({ className, children, testId, ...otherProps }) {
  const ulClassNameList = classNames('c-media-grid', className).trim()
  return (
    <ul className={ulClassNameList} {...otherProps} data-testid={testId}>
      {children}
    </ul>
  )
}

MediaGrid.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
  testId: PropTypes.string,
}

/**
 * The MediaGridItem component is internally used to wrap the item content
 * in a link and display it inside the {@link MediaGrid}.
 *
 * @param {string} href - The URL that the hyperlink points to for the {@link MediaGridItem}.
 * @param {HTMLImageElement} children - Accepts only one image element.
 * @param {string} [label] - Render an optional label underneath the image.
 * @param {string} [testId] - `[data-testid]` attribute on main element.
 * @param {Object} [otherProps] - Other props to be passed to the HTMLAnchorElement.
 * @returns {JSX.Element} - The {@link MediaGridItem} component.
 * @constructor
 */
function MediaGridItem({ href, label, children, ...otherProps }) {
  return (
    <li className="c-media-grid__item">
      <a className="c-media-grid__link" href={href} {...otherProps}>
        {React.Children.map(children, (child) => React.cloneElement(child, { className: 'c-media-grid__image' }))}
        {label && <span className="c-media-grid__label">{label}</span>}
      </a>
    </li>
  )
}

MediaGridItem.propTypes = {
  href: PropTypes.string.isRequired,
  /**
   * Only allow one child.
   */
  children: PropTypes.element.isRequired,
  /**
   * Convenience prop for `has-labels` from parent component.
   */
  label: PropTypes.string,
  testId: PropTypes.string,
}

MediaGridItem.displayName = 'MediaGrid.Item'
MediaGrid.Item = MediaGridItem
